import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import { defaultIssueContentRestrictions, IPublicationContentRestrictions, IssueContentRestrictions } from '@pressreader/content-types';
import { purchaseDone } from '@pressreader/purchase';

import {
    issueRestrictionsUpdated,
    loadGlobalRestrictionsBegan,
    loadGlobalRestrictionsCancelled,
    loadGlobalRestrictionsDone,
    loadGlobalRestrictionsFailed,
    loadIssueRestrictionsBegan,
    loadIssueRestrictionsCancelled,
    loadIssueRestrictionsDone,
    loadIssueRestrictionsFailed,
} from '../actions/restrictions';

interface State {
    publications: {
        status: OperationStatus;
        restrictions: { [cid: string]: IPublicationContentRestrictions };
    };

    issues: {
        [cidDateKey: string]: {
            status: OperationStatus;
            restrictions: IssueContentRestrictions;
        };
    };
}

const initialState: State = {
    publications: {
        status: OperationStatus.None,
        restrictions: {},
    },
    issues: {},
};

function getIssueRestrictions(state: State, cidDateKey: string) {
    if (state.issues[cidDateKey]) {
        return state.issues[cidDateKey];
    }

    return { status: OperationStatus.None, restrictions: { ...defaultIssueContentRestrictions } };
}

function settlePendingGlobalRestrictions(state: State, status: OperationStatus): State {
    if (state.publications.status !== OperationStatus.Pending) {
        return state;
    }

    return { ...state, publications: { ...state.publications, status } };
}

function settlePendingIssueRestrictions(state: State, cidDateKey: string, status: OperationStatus): State {
    const issueRestrictions = state.issues[cidDateKey];
    if (issueRestrictions?.status !== OperationStatus.Pending) {
        return state;
    }

    return { ...state, issues: { ...state.issues, [cidDateKey]: { ...issueRestrictions, status } } };
}

function mergeRestrictions(
    base: IPublicationContentRestrictions | undefined,
    updates: Partial<IPublicationContentRestrictions>,
): IPublicationContentRestrictions {
    const merged = {
        anonymousContentRestricted: updates.anonymousContentRestricted || (base?.anonymousContentRestricted ?? false),
        sound: updates.sound || (base?.sound ?? false),
        blogging: updates.blogging || (base?.blogging ?? false),
        comments: updates.comments || (base?.comments ?? false),
        emailSharing: updates.emailSharing || (base?.emailSharing ?? false),
        readItLater: updates.readItLater || (base?.readItLater ?? false),
        smartFlow: updates.smartFlow || (base?.smartFlow ?? false),
        translation: updates.translation || (base?.translation ?? false),
    };

    return merged;
}

const reducer = createReducer(
    initialState,
    createActionHandler(userChangeDone, state => ({
        ...state,
        publications: { status: OperationStatus.None, restrictions: {} },
        issues: {},
    })),
    createActionHandler(purchaseDone, state => ({
        ...state,
        publications: { status: OperationStatus.None, restrictions: {} },
        issues: {},
    })),
    createActionHandler(loadGlobalRestrictionsBegan, state => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Pending,
        },
    })),
    createActionHandler(loadGlobalRestrictionsDone, (state, { payload }) => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Done,
            restrictions: payload.restrictions,
        },
    })),
    createActionHandler(issueRestrictionsUpdated, (state, { payload }) => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Done,
            restrictions: {
                ...state.publications.restrictions,
                [payload.cid]: mergeRestrictions(state.publications.restrictions[payload.cid], payload.restrictions),
            },
        },
    })),
    // The issue-info loader publishes restriction data on its own (`issueRestrictionsUpdated`, `loadIssueRestrictionsDone`
    // in sagas/utils/loaders.ts) and the selectors serve restrictions only from `Done`, so a cancellation or a late
    // failure of an overlapping request must never overwrite a settled status.
    createActionHandler(loadGlobalRestrictionsFailed, state => settlePendingGlobalRestrictions(state, OperationStatus.Failed)),
    createActionHandler(loadGlobalRestrictionsCancelled, state => settlePendingGlobalRestrictions(state, OperationStatus.None)),
    createActionHandler(loadIssueRestrictionsBegan, (state, { payload }) => {
        const issueRestrictions = { ...getIssueRestrictions(state, payload.cidDateKey) };
        issueRestrictions.status = OperationStatus.Pending;

        return {
            ...state,
            issues: {
                ...state.issues,
                [payload.cidDateKey]: issueRestrictions,
            },
        };
    }),
    createActionHandler(loadIssueRestrictionsDone, (state, { payload }) => {
        const issueRestrictions = { ...getIssueRestrictions(state, payload.cidDateKey) };
        issueRestrictions.status = OperationStatus.Done;
        issueRestrictions.restrictions = payload.restrictions;

        return {
            ...state,
            issues: {
                ...state.issues,
                [payload.cidDateKey]: issueRestrictions,
            },
        };
    }),
    createActionHandler(loadIssueRestrictionsFailed, (state, { payload }) =>
        settlePendingIssueRestrictions(state, payload.cidDateKey, OperationStatus.Failed),
    ),
    createActionHandler(loadIssueRestrictionsCancelled, (state, { payload }) =>
        settlePendingIssueRestrictions(state, payload.cidDateKey, OperationStatus.None),
    ),
);

export { reducer, State };
